import {
  FetchArgs,
  BaseQueryFn,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query";
import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import humps from "humps";
import { Mutex } from "async-mutex";

import { refreshTokenIfExpired } from "../utils/refreshTokenIfExpired";

// Create a mutex to prevent multiple simultaneous refresh attempts
const mutex = new Mutex();

export function getBaseQueryWithCamelize(
  baseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    // eslint-disable-next-line @typescript-eslint/ban-types
    {},
    FetchBaseQueryMeta
  >
) {
  const baseQueryWithCamelize: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
  > = async (args, api, extraOptions = {}) => {
    // decamelize request fields
    if (typeof args !== "string") {
      args.body = humps.decamelizeKeys(args.body);
    }

    // Wait for the mutex if there's an ongoing refresh
    await mutex.waitForUnlock();

    let result = await baseQuery(args, api, extraOptions);

    // If we get a 401 or 403, try to refresh the token and retry once
    if (
      result.error &&
      (result.error.status === 401 || result.error.status === 403)
    ) {
      // Check if the mutex is already locked (another request is refreshing)
      if (!mutex.isLocked()) {
        const release = await mutex.acquire();
        try {
          // Try to refresh the token
          const newToken = await refreshTokenIfExpired();
          if (newToken) {
            // Retry the original request with the new token
            result = await baseQuery(args, api, extraOptions);
          }
        } catch (error) {
          // If refresh fails, the refreshTokenIfExpired function will handle logout
        } finally {
          release();
        }
      } else {
        // Wait for the other request to finish refreshing and retry
        await mutex.waitForUnlock();
        result = await baseQuery(args, api, extraOptions);
      }
    }

    if (result.data) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result.data = humps.camelizeKeys(result.data as any);
    }
    return result;
  };
  return baseQueryWithCamelize;
}

export const baseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_WARRENG_URL}broker-portal/v1/`,
  prepareHeaders: async (headers) => {
    const token = await refreshTokenIfExpired();
    if (token) {
      headers.set("Authorization", `JWT ${token}`);
    }
    return headers;
  },
});

export const baseQueryWithCamelize = getBaseQueryWithCamelize(baseQuery);
